import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Grid, Card, CardContent, Stack, Skeleton,
  CardActionArea, useTheme,
} from "@mui/material";
import {
  ResponsiveContainer,
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import AddIcon from "@mui/icons-material/Add";
import { api } from "../../api/client.js";
import { BadgeChip, botStateToBadgeColor, botStateLabel } from "../../components/BadgeChip.js";
import { MetricCard } from "../../components/MetricCard.js";
import { GlowButton } from "../../components/GlowButton.js";
import { BotLetterMark } from "../../components/BotLetterMark.js";
import { CHART_COLORS, BRAND } from "../../theme/index.js";
import type { Bot } from "@chatfab/shared";

interface DayPoint   { date: string; sessions: number; messages: number }
interface DailyUsage { date: string; messages: number; tokens_input: number; tokens_output: number }
interface BotBreakdown { name: string; sessions: number; messages: number }
interface HourPoint  { hour: number; count: number }

interface OverviewStats {
  active_bots:       number;
  total_bots:        number;
  total_documents:   number;
  total_web_sources: number;
  total_chunks:      number;
  messages_used:   number;
  messages_limit:  number;
  period:          string;
  total_sessions:  number;
  recent_bots:     Bot[];
  sessions_by_day: DayPoint[];
  daily_usage:     DailyUsage[];
  sessions_by_bot: BotBreakdown[];
  peak_hours:      HourPoint[];
  type_split:      { direct: number; embed: number };
}

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string) {
  const [, m, d] = iso.split("-");
  return `${m}/${d}`;
}

function fmtHour(h: number) {
  if (h === 0)  return "12a";
  if (h < 12)  return `${h}a`;
  if (h === 12) return "12p";
  return `${h - 12}p`;
}

function fmtK(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

// ── chart card ───────────────────────────────────────────────────────────────

function ChartCard({ title, children, height = 220 }: { title: string; children: React.ReactNode; height?: number }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dotOpacity = isDark ? 0.04 : 0.06;
  const dotColor = isDark ? `rgba(255,255,255,${dotOpacity})` : `rgba(0,0,0,${dotOpacity})`;

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h5" mb={2}>{title}</Typography>
        <Box sx={{
          height,
          backgroundImage: `radial-gradient(circle, ${dotColor} 1px, transparent 1px)`,
          backgroundSize: "20px 20px",
          borderRadius: "8px",
          padding: "4px",
        }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

// ── charts ───────────────────────────────────────────────────────────────────

function SessionsChart({ data }: { data: DayPoint[] }) {
  const theme = useTheme();
  const color = theme.palette.primary.main;
  const grid  = theme.palette.divider;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip
          formatter={(v: number, name: string) => [v, name === "sessions" ? "Sessions" : "Messages"]}
          labelFormatter={fmtDate}
          contentStyle={{ fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Area type="monotone" dataKey="sessions" stroke={color} fill="url(#gradSessions)" strokeWidth={2} dot={false} name="sessions" />
        <Area type="monotone" dataKey="messages" stroke={BRAND.yellow} fill="none" strokeWidth={1.5} strokeDasharray="4 2" dot={false} name="messages" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function TokensChart({ data }: { data: DailyUsage[] }) {
  const theme = useTheme();
  const grid  = theme.palette.divider;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 4, right: 8, left: -8, bottom: 0 }}>
        <defs>
          <linearGradient id="gradIn"  x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={BRAND.green}  stopOpacity={0.25} />
            <stop offset="95%" stopColor={BRAND.green}  stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradOut" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%"  stopColor={BRAND.yellow} stopOpacity={0.25} />
            <stop offset="95%" stopColor={BRAND.yellow} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} interval="preserveStartEnd" />
        <YAxis tickFormatter={fmtK} tick={{ fontSize: 11 }} />
        <Tooltip
          formatter={(v: number, name: string) => [fmtK(v), name === "tokens_input" ? "Input tokens" : "Output tokens"]}
          labelFormatter={fmtDate}
          contentStyle={{ fontSize: 12 }}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v) => v === "tokens_input" ? "Input tokens" : "Output tokens"} />
        <Area type="monotone" dataKey="tokens_input"  stroke={BRAND.green}  fill="url(#gradIn)"  strokeWidth={2} dot={false} name="tokens_input" />
        <Area type="monotone" dataKey="tokens_output" stroke={BRAND.yellow} fill="url(#gradOut)" strokeWidth={2} dot={false} name="tokens_output" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

function BotSessionsChart({ data }: { data: BotBreakdown[] }) {
  const theme = useTheme();
  const grid  = theme.palette.divider;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={90} />
        <Tooltip contentStyle={{ fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="sessions" name="Sessions" fill={BRAND.cyan}   radius={[0, 3, 3, 0]} />
        <Bar dataKey="messages" name="Messages" fill={BRAND.yellow} radius={[0, 3, 3, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PeakHoursChart({ data }: { data: HourPoint[] }) {
  const theme = useTheme();
  const grid  = theme.palette.divider;
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
        <XAxis dataKey="hour" tickFormatter={fmtHour} tick={{ fontSize: 10 }} interval={2} />
        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
        <Tooltip
          formatter={(v: number) => [v, "Sessions"]}
          labelFormatter={(h: number) => `${fmtHour(h)} UTC`}
          contentStyle={{ fontSize: 12 }}
        />
        <Bar dataKey="count" name="Sessions" radius={[3, 3, 0, 0]}>
          {data.map((entry, i) => (
            <Cell
              key={i}
              fill={entry.count === max ? theme.palette.primary.main : `${theme.palette.primary.main}55`}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function TypeSplitChart({ data }: { data: { direct: number; embed: number } }) {
  const total = data.direct + data.embed;
  if (total === 0) {
    return (
      <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="caption" color="text.disabled">No sessions yet</Typography>
      </Box>
    );
  }
  const pieData = [
    { name: "Direct", value: data.direct },
    { name: "Embed",  value: data.embed  },
  ].filter(d => d.value > 0);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={pieData} cx="50%" cy="50%" innerRadius="45%" outerRadius="70%"
          dataKey="value" nameKey="name" paddingAngle={2}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {pieData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
        </Pie>
        <Tooltip formatter={(v: number) => [v, "Sessions"]} contentStyle={{ fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── loading skeleton ─────────────────────────────────────────────────────────

function OverviewSkeleton() {
  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        {[1,2,3,4,5].map(i => (
          <Grid item xs={12} sm={6} md={i <= 3 ? 4 : 6} key={i}>
            <Skeleton variant="rounded" height={100} />
          </Grid>
        ))}
      </Grid>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={8}><Skeleton variant="rounded" height={280} /></Grid>
        <Grid item xs={12} md={4}><Skeleton variant="rounded" height={280} /></Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}><Skeleton variant="rounded" height={260} /></Grid>
        <Grid item xs={12} md={6}><Skeleton variant="rounded" height={260} /></Grid>
      </Grid>
    </Box>
  );
}

// ── main page ────────────────────────────────────────────────────────────────

export function OverviewPage() {
  const nav = useNavigate();
  const theme = useTheme();
  const [stats, setStats]     = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<OverviewStats>("/api/v1/dashboard/overview")
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h2">Overview</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Last 30 days · all bots
        </Typography>
      </Box>

      {loading ? <OverviewSkeleton /> : stats && (
        <>
          {/* ── Metric cards ── */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={6} sm={4} md={2.4}>
              <MetricCard label="Active Bots" value={stats.active_bots} accent="cyan">
                <Typography variant="caption" color="text.secondary">{`of ${stats.total_bots} total`}</Typography>
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <MetricCard label="Sessions (30d)" value={stats.total_sessions.toLocaleString()} accent="cyan" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <MetricCard label="Messages (month)" value={stats.messages_used.toLocaleString()} accent="yellow">
                {stats.messages_limit > 0 && (
                  <Typography variant="caption" color="text.secondary">{`of ${stats.messages_limit.toLocaleString()} limit`}</Typography>
                )}
              </MetricCard>
            </Grid>
            <Grid item xs={6} sm={6} md={2.4}>
              <MetricCard label="Data Sources" value={(stats.total_documents + stats.total_web_sources).toLocaleString()} accent="yellow">
                <Typography variant="caption" color="text.secondary">
                  {`${stats.total_documents} files · ${stats.total_web_sources} web`}
                </Typography>
              </MetricCard>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard label="Chunks Stored" value={fmtK(stats.total_chunks)} accent="cyan">
                <Typography variant="caption" color="text.secondary">indexed for search</Typography>
              </MetricCard>
            </Grid>
          </Grid>

          {/* ── Sessions + tokens over time ── */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={8}>
              <ChartCard title="Sessions & Messages (30d)" height={220}>
                <SessionsChart data={stats.sessions_by_day} />
              </ChartCard>
            </Grid>
            <Grid item xs={12} md={4}>
              <ChartCard title="Session Type" height={220}>
                <TypeSplitChart data={stats.type_split} />
              </ChartCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12}>
              <ChartCard title="Token Usage (30d)" height={200}>
                <TokensChart data={stats.daily_usage} />
              </ChartCard>
            </Grid>
          </Grid>

          {/* ── Bot breakdown + peak hours ── */}
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12} md={stats.sessions_by_bot.length > 0 ? 7 : 12}>
              <ChartCard title="Peak Hours (UTC)" height={200}>
                <PeakHoursChart data={stats.peak_hours} />
              </ChartCard>
            </Grid>
            {stats.sessions_by_bot.length > 0 && (
              <Grid item xs={12} md={5}>
                <ChartCard title="Sessions per Bot" height={200}>
                  <BotSessionsChart data={stats.sessions_by_bot} />
                </ChartCard>
              </Grid>
            )}
          </Grid>

          {/* ── Recent bots ── */}
          <Typography variant="h5" mb={2}>Recent Bots</Typography>
          {!stats.recent_bots?.length ? (
            <Box sx={{ py: 4, textAlign: "center", border: `1px dashed ${theme.palette.divider}`, borderRadius: "6px" }}>
              <Typography color="text.secondary" mb={2}>No bots yet.</Typography>
              <GlowButton startIcon={<AddIcon />} onClick={() => nav("/dashboard/bots")}>
                Create your first bot
              </GlowButton>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {stats.recent_bots.map(bot => (
                <Grid item xs={12} sm={6} md={4} key={bot.bot_id}>
                  <Card variant="outlined">
                    <CardActionArea onClick={() => nav(`/dashboard/bots/${bot.bot_id}`)}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                          <BotLetterMark name={bot.name} size={36} />
                          <BadgeChip label={botStateLabel(bot.state)} color={botStateToBadgeColor(bot.state)} />
                        </Stack>
                        <Typography variant="h5" noWrap>{bot.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.max(0, bot.document_count ?? 0)} sources · /{bot.slug}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  );
}
